import { HashUtils } from "src/libs/hash";
import { inject, injectable } from "tsyringe";
import { autoBind } from "src/shared/decorators/autobind";
import {
    GoogleTagManagerService,
    gtmElementType,
    gtmEvent,
    gtmEventType,
    gtmSectionType,
    gtmSubSectionType,
    gtmSubZone,
    gtmZone,
    gtmZoneType,
} from "src/shared/services/google-tag-manager/google-tag-manager-service";
import type { TagManagerEvent } from "src/shared/services/google-tag-manager/google-tag-manager-service";
import { GtmPageViewService } from "src/shared/services/google-tag-manager/page-view/gtm-page-view-service";

@injectable()
export class GtmTrackingService {
    constructor(
        @inject("GoogleTagManagerService")
        private gtmService: GoogleTagManagerService
    ) {}

    @autoBind
    tracking(tagEvent: TagManagerEvent) {
        this.gtmService.triggerEvent(tagEvent);
    }

    @autoBind
    trackingPage() {
        const params = GtmPageViewService.getPageParameters();
        if (params.pageTitle && params.classPage) {
            this.gtmService.triggerEvent({
                event: gtmEvent.pageView,
                ...params,
            });
        }
    }

    @autoBind
    trackingLogin(userIdentity: string) {
        HashUtils.sha256(userIdentity).then((hash) => {
            this.gtmService.triggerEvent({
                event: gtmEvent.virtualEvent,
                zoneType: gtmZoneType.login,
                zone: gtmZone.startLogin,
                subZone: gtmSubZone.start,
                section: gtmSectionType.notAvailable,
                eventType: gtmEventType.loginSuccess,
                elementType: gtmElementType.button,
                value: "iniciar sesión",
                IDUser: hash,
            });
        });
    }

    @autoBind
    trackingLogout() {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.home,
            zone: gtmZone.home,
            subZone: gtmSubZone.home,
            section: gtmSectionType.logout,
            eventType: gtmEventType.click,
            elementType: gtmElementType.button,
            value: "Cerrar sesión",
        });
    }

    @autoBind
    trackingUser(userIdentity: string) {
        HashUtils.sha256(userIdentity).then((hash) => {
            this.gtmService.triggerEvent({
                event: gtmEvent.setUserId,
                userId: hash,
            });
        });
    }

    @autoBind
    trackingHomeCard(value: string) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.home,
            zone: gtmZone.home,
            subZone: gtmSubZone.home,
            section: gtmSectionType.mainTab,
            eventType: gtmEventType.click,
            elementType: gtmElementType.card,
            value,
        });
    }

    @autoBind
    trackingTermsAndConditions() {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.home,
            zone: gtmZone.home,
            subZone: gtmSubZone.home,
            section: gtmSectionType.sidebarMenu,
            eventType: gtmEventType.click,
            elementType: gtmElementType.button,
            value: "Terminos y condiciones",
        });
    }

    @autoBind
    trackingBanner(value: string) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.home,
            zone: gtmZone.home,
            subZone: gtmSubZone.home,
            section: gtmSectionType.banners,
            eventType: gtmEventType.click,
            elementType: gtmElementType.button,
            value,
        });
    }

    @autoBind
    trackingNavigation(
        zoneType: gtmZoneType | string,
        zone: gtmZone | string,
        section: gtmSectionType | string,
        value: string
    ) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: zoneType,
            zone: zone,
            subZone: gtmSubZone.home,
            section,
            eventType: gtmEventType.click,
            elementType: gtmElementType.button,
            value: value,
        });
    }

    @autoBind
    trackingRequests(value: string) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.tabsHomeMain,
            zone: gtmZone.followUpsProcedures,
            subZone: gtmSubZone.start,
            section: gtmSectionType.requestList,
            eventType: gtmEventType.click,
            elementType: gtmElementType.tab,
            value,
        });
    }

    @autoBind
    trackingCoverNote(value: string) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.tabsHomeMain,
            zone: gtmZone.followUpsProcedures,
            subZone: gtmSubZone.coverNote,
            section: gtmSectionType.requestList,
            eventType: gtmEventType.click,
            elementType: gtmElementType.button,
            value,
        });
    }

    @autoBind
    trackingCoverNoteConsultation(value: string) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.tabsHomeMain,
            zone: gtmZone.followUpsProcedures,
            subZone: gtmSubZone.coverNote,
            section: gtmSectionType.consultations,
            eventType: gtmEventType.click,
            elementType: gtmElementType.button,
            value,
        });
    }

    @autoBind
    trackingCoverNoteListDisclaimer(value: string) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.tabsHomeMain,
            zone: gtmZone.followUpsProcedures,
            section: gtmSectionType.modal,
            subZone: gtmSubZone.start,
            eventType: gtmEventType.click,
            elementType: gtmElementType.button,
            value,
        });
    }

    @autoBind
    trackingInsuranceList(value: string) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.insurance,
            zone: gtmZone.start,
            subZone: gtmSubZone.start,
            section: gtmSectionType.notAvailable,
            subSection: gtmSubSectionType.insuranceList,
            eventType: gtmEventType.click,
            elementType: gtmElementType.card,
            value,
        });
    }

    @autoBind
    trackingInsuranceHealth(value: string) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.homeTabAssistance,
            zone: gtmZone.insurances,
            section: gtmSectionType.health,
            subZone: gtmSubZone.download,
            eventType: gtmEventType.click,
            elementType: gtmElementType.button,
            value,
        });
    }

    @autoBind
    trackingMedicineDeliveryBasicData(value: string) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.tabsHomeAssistances,
            zone: gtmZone.deliveryMedicines,
            subZone: gtmSubZone.step1,
            section: gtmSectionType.notAvailable,
            eventType: gtmEventType.click,
            elementType: gtmElementType.button,
            value,
        });
    }

    @autoBind
    trackingMedicineDeliveryLocationData(value: string) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.tabsHomeAssistances,
            zone: gtmZone.deliveryMedicines,
            subZone: gtmSubZone.step2,
            section: gtmSectionType.notAvailable,
            eventType: gtmEventType.click,
            elementType: gtmElementType.button,
            value,
        });
    }

    @autoBind
    trackingMedicineDeliveryDocument(value: string) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.tabsHomeAssistances,
            zone: gtmZone.deliveryMedicines,
            subZone: gtmSubZone.step3,
            section: gtmSectionType.notAvailable,
            eventType: gtmEventType.click,
            elementType: gtmElementType.button,
            value,
        });
    }

    @autoBind
    trackingMedicineDeliveryIntro(
        value: string,
        section: gtmSectionType,
        eventType: gtmEventType,
        elementType: gtmElementType,
        subZone: gtmSubZone = gtmSubZone.start
    ) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.tabsHomeAssistances,
            zone: gtmZone.deliveryMedicines,
            subZone,
            section,
            eventType,
            elementType,
            value,
        });
    }

    @autoBind
    trackingTechnicalInspection(value: string, section: gtmSectionType) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.wellbeing,
            zone: gtmZone.discountTechnicalReview,
            subZone: gtmSubZone.wellbeingDetail,
            section,
            eventType: gtmEventType.click,
            elementType: gtmElementType.link,
            value,
        });
    }

    @autoBind
    trackingWellbeingList(value: string) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.wellbeing,
            zone: gtmZone.wellbeingList,
            subZone: gtmSubZone.start,
            section: gtmSectionType.notAvailable,
            eventType: gtmEventType.click,
            elementType: gtmElementType.card,
            value,
        });
    }

    @autoBind
    trackingWellbeingMultiplus(value: string) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.wellbeing,
            zone: gtmZone.multiPlus,
            subZone: gtmSubZone.wellbeingDetail,
            section: gtmSectionType.download,
            eventType: gtmEventType.click,
            elementType: gtmElementType.button,
            value,
        });
    }

    @autoBind
    trackingWellbeingETNA(value: string, section: gtmSectionType) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.wellbeing,
            zone: gtmZone.batteryETNA,
            subZone: gtmSubZone.wellbeingDetail,
            section,
            eventType: gtmEventType.click,
            elementType: gtmElementType.link,
            value,
        });
    }

    @autoBind
    trackingWellbeingMaintenance(value: string) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.wellbeing,
            zone: gtmZone.maintenance,
            subZone: gtmSubZone.wellbeingDetail,
            section: gtmSectionType.channels,
            eventType: gtmEventType.click,
            elementType: gtmElementType.link,
            value,
        });
    }

    @autoBind
    trackingWellbeingCleaning(value: string) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.wellbeing,
            zone: gtmZone.cleaning,
            subZone: gtmSubZone.wellbeingDetail,
            section: gtmSectionType.channels,
            eventType: gtmEventType.click,
            elementType: gtmElementType.link,
            value,
        });
    }

    @autoBind
    trackingDocumentGroup(value: string) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.tabsHomeMain,
            zone: gtmZone.downloadPolicyOncological,
            subZone: gtmSubZone.policyOncologicalNational,
            section: gtmSectionType.downloadPDF,
            eventType: gtmEventType.click,
            elementType: gtmElementType.download,
            value,
        });
    }

    @autoBind
    trackingTravelerAssistanceModal(value: string) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.start,
            zone: gtmZone.notAvailable,
            section: gtmSectionType.modal,
            subZone: gtmSubZone.notAvailable,
            eventType: gtmEventType.click,
            elementType: gtmElementType.button,
            value: `Asistencia al viajero:${value}`,
        });
    }

    @autoBind
    trackingVehicleAssistanceModal(value: string) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.homeTabAssistance,
            zone: gtmZone.insurances,
            section: gtmSectionType.modal,
            subZone: gtmSubZone.download,
            eventType: gtmEventType.click,
            elementType: gtmElementType.button,
            value: `Asistencia Vehicular:${value}`,
        });
    }

    @autoBind
    trackingVehicleSelected(value: string) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.homeTabAssistance,
            zone: gtmZone.vehicleAssistance,
            subZone: gtmSubZone.selectVehicle,
            section: gtmSectionType.start,
            eventType: gtmEventType.click,
            elementType: gtmElementType.button,
            value,
        });
    }

    @autoBind
    trackingVehicleSelectedAssistanceType(value: string) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.homeTabAssistance,
            zone: gtmZone.vehicleAssistance,
            subZone: gtmSubZone.selectAssistanceType,
            section: gtmSectionType.start,
            eventType: gtmEventType.click,
            elementType: gtmElementType.button,
            value,
        });
    }

    @autoBind
    trackingVehicleRechargeBattery(value: string) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.homeTabAssistance,
            zone: gtmZone.vehicleAssistance,
            subZone: gtmSubZone.rechargeBattery,
            section: gtmSectionType.start,
            eventType: gtmEventType.click,
            elementType: gtmElementType.button,
            value,
        });
    }

    @autoBind
    trackingVehicleTireChange(value: string) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.homeTabAssistance,
            zone: gtmZone.vehicleAssistance,
            subZone: gtmSubZone.tireChange,
            section: gtmSectionType.start,
            eventType: gtmEventType.click,
            elementType: gtmElementType.button,
            value,
        });
    }

    @autoBind
    trackingVehicleEnd(
        value: string,
        subZone: gtmSubZone | string = gtmSubZone.vehicleAssistance,
        section = gtmSectionType.start,
        eventType = gtmEventType.click,
        elementType = gtmElementType.button
    ) {
        const _subZone = subZone
            .replace("AuxilioMecanicoServiceExt-", "")
            .replace("ServiceExt", "");
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.homeTabAssistance,
            zone: gtmZone.vehicleAssistance,
            subZone: _subZone,
            section,
            eventType,
            elementType,
            value,
        });
    }

    @autoBind
    trackingVehicheFinishPage(
        assitanceType: string,
        eventType: gtmEventType = gtmEventType.success
    ) {
        const type = assitanceType
            .replace("AuxilioMecanicoServiceExt-", "")
            .replace("ServiceExt", "");
        const classPage =
            eventType === gtmEventType.success
                ? `HomeTabsAsistencias:AsistenciaVehicular:${type}:SolicitudRegistrada`
                : `HomeTabsAsistencias:AsistenciaVehicular:${type}`;

        this.gtmService.triggerEvent({
            event: gtmEvent.pageView,
            pageTitle: "Asistencia Vehicular",
            classPage,
            pageLocation: `${
                window.location.origin
            }${GtmPageViewService.getLocationPage()}`,
        });
    }

    @autoBind
    trackingSurveySatisfactionModal(value: string) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.home,
            zone: gtmZone.home,
            subZone: gtmSubZone.home,
            section: gtmSectionType.qualifyService,
            eventType: gtmEventType.click,
            elementType: gtmElementType.button,
            value,
        });
    }

    @autoBind
    trackingAssistancesPreview(value: string) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.home,
            zone: gtmZone.home,
            subZone: gtmSubZone.home,
            section: gtmSectionType.assistanceTab,
            eventType: gtmEventType.click,
            elementType: gtmElementType.link,
            value,
        });
    }

    @autoBind
    trackingHealthCardElement(
        value: string,
        elementType = gtmElementType.link
    ) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.tabsHomeAssistances,
            zone: gtmZone.healthCard,
            subZone: gtmSubZone.start,
            section: gtmSectionType.notAvailable,
            eventType: gtmEventType.click,
            elementType,
            value,
        });
    }

    @autoBind
    trackingInsuracenLife(
        value: string,
        subZone = gtmSubZone.home,
        eventType = gtmEventType.click,
        elementType = gtmElementType.link,
        section = gtmSectionType.detail,
        subSection = gtmSubSectionType.tabsMain
    ) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.insurance,
            zone: gtmZone.insuranceLife,
            subZone,
            section,
            subSection,
            eventType,
            elementType,
            value,
        });
    }

    @autoBind
    trackingInsuracenMortgate(
        value: string,
        subZone = gtmSubZone.home,
        eventType = gtmEventType.click,
        elementType = gtmElementType.link
    ) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.insurance,
            zone: gtmZone.insuranceMortage,
            subZone,
            section: gtmSectionType.detail,
            subSection: gtmSubSectionType.tabsMain,
            eventType,
            elementType,
            value,
        });
    }

    @autoBind
    trackingInsuracenRenta(
        value: string,
        subZone = gtmSubZone.home,
        eventType = gtmEventType.click,
        elementType = gtmElementType.link,
        section = gtmSectionType.detail,
        subSection = gtmSubSectionType.tabsMain
    ) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.insurance,
            zone: gtmZone.insuranceRental,
            subZone,
            section,
            subSection,
            eventType,
            elementType,
            value,
        });
    }

    @autoBind
    trackingInsuracenBurial(
        value: string,
        subZone = gtmSubZone.home,
        eventType = gtmEventType.click,
        elementType = gtmElementType.link,
        section = gtmSectionType.detail
    ) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.insurance,
            zone: gtmZone.insuranceBurial,
            subZone,
            section,
            subSection: gtmSubSectionType.tabsMain,
            eventType,
            elementType,
            value,
        });
    }

    @autoBind
    trackingSinisterHome(
        value: string,
        subZone = gtmSubZone.home,
        eventType = gtmEventType.click,
        elementType = gtmElementType.link,
        section = gtmSectionType.detail
    ) {
        this.gtmService.triggerEvent({
            event: gtmEvent.virtualEvent,
            zoneType: gtmZoneType.tabsHomeMain,
            zone: gtmZone.sinisterHome,
            subZone,
            section,
            subSection: gtmSubSectionType.tabsMain,
            eventType,
            elementType,
            value,
        });
    }
}
